import moment from 'moment';

export const isNewYearDiscountEnabled =
  moment() >=
    moment(
      process.env.REACT_APP_NEW_YEAR_DISCOUNT_START_DATE_2026,
      'YYYY-MM-DD'
    ).startOf('day') &&
  moment() <=
    moment(
      process.env.REACT_APP_NEW_YEAR_DISCOUNT_END_DATE_2026,
      'YYYY-MM-DD'
    ).endOf('day');

export const calculateDiscountData = (minutes: number, amount: number) => {
  let discountPercentage: number | undefined;
  let percentageAmount: number | undefined;

  if (minutes >= 60 * 60) {
    discountPercentage = 10;
    percentageAmount = (amount * discountPercentage) / 100;
  } else if (minutes >= 30 * 60) {
    discountPercentage = 7;
    percentageAmount = (amount * discountPercentage) / 100;
  } else if (minutes >= 15 * 60) {
    discountPercentage = 5;
    percentageAmount = (amount * discountPercentage) / 100;
  }

  return {
    percentage: discountPercentage,
    discountAmount: percentageAmount,
  };
};

export const getNewYearDiscountAmountData = (data: {
  userRegistrationDate: string;
  amount: number;
  packageMinutes?: number;
  noDefault?: boolean;
}) => {
  const { userRegistrationDate, amount, packageMinutes, noDefault } = data;

  if (!userRegistrationDate) return undefined;

  let newAmount = amount;
  let previousAmount: number | undefined;
  let discountPercentage: number | undefined;
  let percentageAmount = (amount * 10) / 100; // default 10 percent

  const userRegistrationDateMoment = moment(userRegistrationDate);
  const minDate = moment(
    process.env.REACT_APP_NEW_YEAR_DISCOUNT_START_DATE_2026,
    'YYYY-MM-DD'
  ).startOf('day');

  const maxDate = moment(
    process.env.REACT_APP_NEW_YEAR_DISCOUNT_END_DATE_2026,
    'YYYY-MM-DD'
  ).endOf('day');

  const isInRange = userRegistrationDateMoment.isBetween(
    minDate,
    maxDate,
    undefined,
    '[]'
  );

  if (isNewYearDiscountEnabled) {
    if (isInRange) {
      previousAmount = amount + percentageAmount;
      newAmount = previousAmount;

      if (!noDefault) {
        newAmount = previousAmount - percentageAmount;
      }

      if (packageMinutes) {
        const { discountAmount, percentage } = calculateDiscountData(
          packageMinutes,
          previousAmount
        );

        if (discountAmount && percentage) {
          discountPercentage = percentage;
          percentageAmount = discountAmount;
          newAmount = previousAmount - discountAmount;
        }
      }
    } else if (
      userRegistrationDateMoment < minDate &&
      userRegistrationDateMoment < maxDate
    ) {
      previousAmount = amount;
      newAmount = previousAmount;

      if (!noDefault) {
        newAmount = previousAmount - percentageAmount;
      }

      if (packageMinutes) {
        const { discountAmount, percentage } = calculateDiscountData(
          packageMinutes,
          amount
        );

        if (discountAmount && percentage) {
          discountPercentage = percentage;
          percentageAmount = discountAmount;
          newAmount = previousAmount - discountAmount;
        }
      }
    }
  }

  return {
    discountPercentage,
    previousAmount,
    amount: newAmount,
  };
};

// ---------------------------------------------------------------------------
// Monthly plan discounts
// ---------------------------------------------------------------------------
// Monthly subscription plans discount the per-lesson rate based on how many
// lessons per month are committed to (12+ lessons → 5% off). This mirrors the
// shape of getNewYearDiscountAmountData so the Monthly Plans tab can consume it
// the same way the One-Time tab consumes the new-year discount.
export const calculateMonthlyPlanDiscountData = (
  lessonsPerMonth: number
): number | undefined => {
  if (lessonsPerMonth >= 12) {
    return 5;
  }

  return undefined;
};

export const getMonthlyPlanDiscountAmountData = (data: {
  // Per-lesson rate to discount.
  amount: number;
  lessonsPerMonth: number;
}) => {
  const { amount, lessonsPerMonth } = data;

  if (!isNewYearDiscountEnabled) {
    return {
      discountPercentage: undefined,
      previousAmount: undefined,
      amount,
    };
  }

  // Mirror the One-Time tab's new-year promo mechanic: when the new-year
  // discount is live, the base rate is first inflated by the default 10% so the
  // pre-discount ("previous") price shown struck-through matches the One-Time
  // tab, and the monthly-plan discount is then taken off that inflated rate.
  const baseAmount = isNewYearDiscountEnabled
    ? amount + (amount * 10) / 100
    : amount;

  const discountPercentage = calculateMonthlyPlanDiscountData(lessonsPerMonth);

  if (!discountPercentage) {
    return {
      // When the new-year promo is live there is still a price difference (the
      // 10% inflation) to surface, even without a lessons-tier discount.
      discountPercentage: undefined,
      previousAmount: undefined,
      amount: baseAmount,
    };
  }

  const newAmount = baseAmount - (baseAmount * discountPercentage) / 100;

  return {
    discountPercentage,
    previousAmount: baseAmount,
    amount: newAmount,
  };
};

// ---------------------------------------------------------------------------
// Quarterly plan discounts
// ---------------------------------------------------------------------------
// Quarterly subscription plans (billed every 3 months) reward longer
// commitments with a larger per-lesson discount than the monthly plan. Tiers
// are keyed by lessons-PER-MONTH (12/8/4 -> 10/7/5), matching the quarterly
// cards on the Monthly Plans tab (36/24/12 lessons per 3 months). Mirrors the
// backend getQuarterlyPlanDiscountAmountData in rates.helper.ts.
export const calculateQuarterlyPlanDiscountData = (
  lessonsPerMonth: number
): number | undefined => {
  if (lessonsPerMonth === 12) {
    return 10;
  }

  if (lessonsPerMonth === 8) {
    return 7;
  }

  if (lessonsPerMonth === 4) {
    return 5;
  }

  return undefined;
};

export const getQuarterlyPlanDiscountAmountData = (data: {
  // Per-lesson rate to discount.
  amount: number;
  lessonsPerMonth: number;
}) => {
  const { amount, lessonsPerMonth } = data;

  // Unlike the monthly plan, the quarterly discount applies year-round (it is a
  // permanent perk for committing to 3 months). The 10% new-year inflation that
  // produces the struck-through "previous" price is only added while the promo
  // is live, mirroring the monthly helper's "previous" price.
  const baseAmount = isNewYearDiscountEnabled
    ? amount + (amount * 10) / 100
    : amount;

  const discountPercentage = calculateQuarterlyPlanDiscountData(
    lessonsPerMonth
  );

  if (!discountPercentage) {
    return {
      discountPercentage: undefined,
      previousAmount: undefined,
      amount: baseAmount,
    };
  }

  const newAmount = baseAmount - (baseAmount * discountPercentage) / 100;

  return {
    discountPercentage,
    // Only surface a struck-through "previous" price when the promo inflated the base.
    previousAmount: isNewYearDiscountEnabled ? baseAmount : undefined,
    amount: newAmount,
  };
};

export default getNewYearDiscountAmountData;
